import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Message } from "@/types/chat";

interface SavedChat {
  id: string;
  title: string;
}

export function useChatArchive() {
  const [savedChats, setSavedChats] = useState<SavedChat[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    loadSavedChats();
  }, []);

  const loadSavedChats = async () => {
    try {
      const { data: chats, error } = await supabase
        .from('chat_conversations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedChats: SavedChat[] = (chats || []).map(chat => ({
        id: chat.id,
        title: chat.title
      }));

      setSavedChats(formattedChats);
    } catch (error) {
      console.error('Error loading saved chats:', error);
      toast({
        variant: "destructive",
        title: "Feil ved lasting av chatter",
        description: "Kunne ikke laste lagrede chatter. Vennligst prøv igjen senere."
      });
    }
  };

  const handleSaveChat = async (messages: Message[]) => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw userError;

      const title = messages[messages.length - 1]?.content.slice(0, 50) + "..." || "Ny chat";

      const { data: conversation, error: conversationError } = await supabase
        .from('chat_conversations')
        .insert({
          title,
          user_id: user.id
        })
        .select()
        .single();

      if (conversationError) throw conversationError;

      const messagesToSave = messages.map(msg => ({
        conversation_id: conversation.id,
        content: msg.content,
        role: msg.role,
        user_id: user.id
      }));

      const { error: messagesError } = await supabase
        .from('chat_messages')
        .insert(messagesToSave);

      if (messagesError) throw messagesError;

      await loadSavedChats();

      toast({
        title: "Chat lagret",
        description: "Chatten har blitt lagret og kan finnes i arkivet."
      });
    } catch (error) {
      console.error('Error saving chat:', error);
      toast({
        variant: "destructive",
        title: "Feil ved lagring",
        description: "Kunne ikke lagre chatten. Vennligst prøv igjen senere."
      });
    }
  };

  const handleDeleteChat = async (chatId: string) => {
    try {
      // First delete all messages associated with the chat
      const { error: messagesError } = await supabase
        .from('chat_messages')
        .delete()
        .eq('conversation_id', chatId);

      if (messagesError) throw messagesError;

      // Then delete the chat conversation
      const { error: conversationError } = await supabase
        .from('chat_conversations')
        .delete()
        .eq('id', chatId);

      if (conversationError) throw conversationError;

      // Update the local state
      setSavedChats(prev => prev.filter(chat => chat.id !== chatId));

    } catch (error) {
      console.error('Error deleting chat:', error);
      throw error;
    }
  };

  const handleRenameChat = async (chatId: string, newTitle: string) => {
    try {
      const { error } = await supabase
        .from('chat_conversations')
        .update({ title: newTitle })
        .eq('id', chatId);

      if (error) throw error;

      // Update local state
      setSavedChats(prev => prev.map(chat => 
        chat.id === chatId ? { ...chat, title: newTitle } : chat
      ));

    } catch (error) {
      console.error('Error renaming chat:', error);
      throw error;
    }
  };

  const loadChat = async (chatId: string) => {
    try {
      const { data: chatMessages, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', chatId)
        .order('created_at');

      if (error) throw error;

      return chatMessages?.map(msg => ({
        id: msg.id,
        role: msg.role as "assistant" | "user",
        content: msg.content,
        created_at: msg.created_at
      })) || [];
    } catch (error) {
      console.error('Error loading chat:', error);
      toast({
        variant: "destructive",
        title: "Feil ved lasting",
        description: "Kunne ikke laste chatten. Vennligst prøv igjen senere."
      });
      return [];
    }
  };

  const resetChat = () => {
    // Reset local chat state
    setSavedChats([]);
  };

  return {
    savedChats,
    handleSaveChat,
    handleDeleteChat,
    handleRenameChat,
    loadChat,
    resetChat
  };
}